import React, { useState, useRef, useCallback } from 'react';
import {
  inspectionsApi,
  ComponentAttribute,
  ComponentComment,
  ComponentPhoto,
  ComponentPhotoAnnotation,
  FindingWithConfidence,
  Finding,
  RoomComponent,
} from '@/api/inspections';
import { Badge, Button, Input, StatusPill, cn } from '@/ui';
import FindingCard from './FindingCard';
import BBoxAnnotationOverlay, { type BBoxFinding } from './BBoxAnnotationOverlay';
import { SuggestionsStrip } from '@/components/library/SuggestionsStrip';
import { AddComponentControl } from './ComponentList/AddComponentControl';
import { ComponentCommentsSection } from './ComponentList/ComponentComments';
import { STATUS_TITLE, nextStatus, resolveStatus } from './ComponentList/_helpers';

interface ComponentListProps {
  inspectionId: string;
  roomId: string;
  /** Room name doubles as the template section — used by SuggestionsStrip. */
  roomName: string;
  components: RoomComponent[];
  findings: Finding[];
  activeComponentId: string | null;
  readOnly: boolean;
  onComponentFocus: (componentId: string | null) => void;
  onComponentChanged: (component: RoomComponent) => void;
  onComponentAdded: (component: RoomComponent) => void;
  onAttributeChanged: (attribute: ComponentAttribute) => void;
  onAttributeAdded: (componentId: string, attribute: ComponentAttribute) => void;
  onAttributeDeleted: (componentId: string, attributeId: string) => void;
  onFindingAdded: (finding: Finding) => void;
  onFindingUpdated: (finding: Finding) => void;
  onFindingDeleted: (findingId: string) => void;
  /**
   * Comment lifecycle — the parent (InspectionView) owns the inspection
   * tree, so each comment mutation bubbles up so the visible state stays
   * consistent with the server. `onCommentDeleted` carries the component
   * id so the parent can splice the comment out without a full refresh.
   */
  onCommentAdded: (componentId: string, comment: ComponentComment) => void;
  onCommentChanged: (componentId: string, comment: ComponentComment) => void;
  onCommentDeleted: (componentId: string, commentId: string) => void;
  /** Called on suggestion-strip errors so the parent can surface a toast. */
  onSuggestionError?: (message: string) => void;
  /**
   * Called by each ComponentRow on mount so the parent can invoke the
   * component's hidden file input from elsewhere (e.g. the main Photo FAB).
   */
  onComponentPhotoTriggerReady?: (componentId: string, trigger: () => void) => void;
  /**
   * Issue #44 — open the SaveToLibrarySheet pre-filled from a finding. The
   * parent (InspectionView) owns the sheet; we forward the section/component
   * context so the entry lands at the right slot in the library.
   */
  onSaveToLibrary?: (finding: Finding, sectionName: string, componentName: string | null) => void;
  /**
   * Bumped by InspectionView after each successful library save so each
   * component's SuggestionsStrip refetches and surfaces the new entry.
   */
  librarySaveVersion?: number;
}

const ComponentList: React.FC<ComponentListProps> = ({
  inspectionId,
  roomId,
  roomName,
  components,
  findings,
  activeComponentId,
  readOnly,
  onComponentFocus,
  onComponentChanged,
  onComponentAdded,
  onAttributeChanged,
  onAttributeAdded,
  onAttributeDeleted,
  onFindingAdded,
  onFindingUpdated,
  onFindingDeleted,
  onCommentAdded,
  onCommentChanged,
  onCommentDeleted,
  onSuggestionError,
  onComponentPhotoTriggerReady,
  onSaveToLibrary,
  librarySaveVersion,
}) => {
  if (components.length === 0) {
    return (
      <div className="mb-4 space-y-2">
        <div className="p-4 border border-dashed border-border rounded-xl text-center">
          <p className="text-caption text-ink-subtle">
            No components defined for this section. Add findings directly below.
          </p>
        </div>
        {!readOnly && (
          <AddComponentControl
            inspectionId={inspectionId}
            roomId={roomId}
            onComponentAdded={onComponentAdded}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 mb-4">
      <div className="flex items-center justify-between">
        <h3 className="text-overline uppercase tracking-wide text-ink-subtle">
          Components
        </h3>
      </div>
      <div className="rounded-xl border border-border-subtle overflow-hidden bg-surface-1 divide-y divide-border-subtle">
        {components.map((component) => {
          const componentFindings = findings.filter((f) => f.componentId === component.id);
          return (
            <ComponentRow
              key={component.id}
              inspectionId={inspectionId}
              roomId={roomId}
              roomName={roomName}
              component={component}
              componentFindings={componentFindings}
              isActive={activeComponentId === component.id}
              readOnly={readOnly}
              onFocus={() =>
                onComponentFocus(activeComponentId === component.id ? null : component.id)
              }
              onComponentChanged={onComponentChanged}
              onAttributeChanged={onAttributeChanged}
              onAttributeAdded={onAttributeAdded}
              onAttributeDeleted={onAttributeDeleted}
              onFindingAdded={onFindingAdded}
              onFindingUpdated={onFindingUpdated}
              onFindingDeleted={onFindingDeleted}
              onCommentAdded={onCommentAdded}
              onCommentChanged={onCommentChanged}
              onCommentDeleted={onCommentDeleted}
              onSuggestionError={onSuggestionError}
              onRegisterPhotoTrigger={
                onComponentPhotoTriggerReady
                  ? (trigger) => onComponentPhotoTriggerReady(component.id, trigger)
                  : undefined
              }
              onSaveToLibrary={
                onSaveToLibrary
                  ? (finding) => onSaveToLibrary(finding, roomName, component.name)
                  : undefined
              }
              librarySaveVersion={librarySaveVersion}
            />
          );
        })}
      </div>
      {!readOnly && (
        <AddComponentControl
          inspectionId={inspectionId}
          roomId={roomId}
          onComponentAdded={onComponentAdded}
        />
      )}
    </div>
  );
};

interface ComponentRowProps {
  inspectionId: string;
  roomId: string;
  roomName: string;
  component: RoomComponent;
  componentFindings: Finding[];
  isActive: boolean;
  readOnly: boolean;
  onFocus: () => void;
  onComponentChanged: (component: RoomComponent) => void;
  onAttributeChanged: (attribute: ComponentAttribute) => void;
  onAttributeAdded: (componentId: string, attribute: ComponentAttribute) => void;
  onAttributeDeleted: (componentId: string, attributeId: string) => void;
  onFindingAdded: (finding: Finding) => void;
  onFindingUpdated: (finding: Finding) => void;
  onFindingDeleted: (findingId: string) => void;
  onCommentAdded: (componentId: string, comment: ComponentComment) => void;
  onCommentChanged: (componentId: string, comment: ComponentComment) => void;
  onCommentDeleted: (componentId: string, commentId: string) => void;
  onSuggestionError?: (message: string) => void;
  /**
   * Called once on mount so the parent can obtain a trigger to programmatically
   * open this component's hidden file input (e.g. from the main Photo FAB).
   * Using a trigger instead of a direct ref avoids cross-component ref juggling.
   */
  onRegisterPhotoTrigger?: (trigger: () => void) => void;
  /**
   * Issue #44 — open the SaveToLibrarySheet pre-filled from a finding on this
   * component. ComponentList already binds the section/component context.
   */
  onSaveToLibrary?: (finding: Finding) => void;
  /** Forwarded to SuggestionsStrip so it refetches after library writes. */
  librarySaveVersion?: number;
}

const ComponentRow: React.FC<ComponentRowProps> = ({
  inspectionId,
  roomId,
  roomName,
  component,
  componentFindings,
  isActive,
  readOnly,
  onFocus,
  onComponentChanged,
  onAttributeChanged,
  onAttributeAdded,
  onAttributeDeleted,
  onFindingAdded,
  onFindingUpdated,
  onFindingDeleted,
  onCommentAdded,
  onCommentChanged,
  onCommentDeleted,
  onSuggestionError,
  onRegisterPhotoTrigger,
  onSaveToLibrary,
  librarySaveVersion,
}) => {
  // ── Per-component attribute schema cache ─────────────────────────────────
  // Keyed by attribute.key → options[]. Initialised from whatever the GET
  // response already hydrated (schema hydration in crud.ts), so fields with
  // known schemas show suggestions instantly with no network call.
  // Keys are lowercased throughout so template-seeded keys ('Material') resolve
  // against AI-generated schema keys ('material') regardless of casing.
  const [schemaOptionsMap, setSchemaOptionsMap] = useState<Record<string, string[]>>(
    () => Object.fromEntries(
      component.attributes
        .filter((a) => a.options && a.options.length > 0)
        .map((a) => [a.key.toLowerCase(), a.options!]),
    ),
  );

  /**
   * Look up the schema options for a template attribute key. The exact
   * lowercase match is tried first; failing that, fall back to the last
   * whitespace-delimited token. Spectora templates prefix generic keys with
   * the component/location ("Driveway Material", "Wall Material", "Ceiling
   * Material") rather than using bare keys ("material"), so the prefixed
   * forms need to resolve against the AI-generated schema's plain `material`
   * key. Underscore-suffixed keys ('roof_material' → 'material') are already
   * pre-mapped at schema-build time inside ensureSchema.
   */
  const lookupSchemaOptions = useCallback(
    (rawKey: string): string[] | null => {
      const lower = rawKey.toLowerCase().trim();
      if (schemaOptionsMap[lower]) return schemaOptionsMap[lower];
      if (lower.includes(' ')) {
        const lastWord = lower.split(/\s+/).slice(-1)[0];
        if (lastWord && schemaOptionsMap[lastWord]) return schemaOptionsMap[lastWord];
      }
      return null;
    },
    [schemaOptionsMap],
  );
  // Guard: fetch the schema at most once per component row lifetime.
  const schemaFetchedRef = useRef(false);

  const ensureSchema = async () => {
    if (schemaFetchedRef.current) return;
    schemaFetchedRef.current = true;
    try {
      const result = await inspectionsApi.getAttributeSchema(roomName, component.name);
      if (result.success && result.data) {
        const next: Record<string, string[]> = {};
        for (const field of result.data.attributes) {
          if (field.options && field.options.length > 0) {
            const k = field.key.toLowerCase();
            next[k] = field.options;
            // Also map by last underscore-segment so AI-generated prefixed keys
            // (e.g. 'driveway_material') resolve against template keys ('material').
            const suffix = k.includes('_') ? k.split('_').slice(-1)[0] : null;
            if (suffix && !(suffix in next)) next[suffix] = field.options;
          }
        }
        setSchemaOptionsMap((prev) => ({ ...prev, ...next }));
      }
    } catch {
      // Non-fatal — fields just stay as free-text.
    }
  };

  // Add-item state. Three exclusive modes share a single bottom slot:
  //   - addingFinding  → finding form
  //   - addingComment  → 'note' or 'limitation' form
  //   - neither        → unified add-row with 4 buttons (Photo / Finding /
  //                       Note / Limitation), Photo just triggers the file
  //                       input directly.
  const [addingFinding, setAddingFinding] = useState(false);
  const [newFindingTitle, setNewFindingTitle] = useState('');
  const [newFindingDescription, setNewFindingDescription] = useState('');
  const [submittingFinding, setSubmittingFinding] = useState(false);
  const [addingComment, setAddingComment] = useState<'note' | 'limitation' | null>(null);
  const [commentBody, setCommentBody] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [cycling, setCycling] = useState(false);
  const [localPhotos, setLocalPhotos] = useState<ComponentPhoto[]>(component.photos ?? []);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  /** Photos currently being analyzed by AI (shows spinner overlay on thumbnail) */
  const [analyzingPhotoIds, setAnalyzingPhotoIds] = useState<Set<string>>(new Set());
  /** Photos whose analysis returned no findings (shows "nothing flagged" state) */
  const [noFindingPhotoIds, setNoFindingPhotoIds] = useState<Set<string>>(new Set());
  /** Per-finding confidence scores — only present for this session (not persisted).
   *  Keyed by findingId → 0–1 float. Passed to FindingCard as transient UI hint. */
  const [findingConfidences, setFindingConfidences] = useState<Map<string, number>>(new Map());
  /** When false only the top AI draft is visible; inspector taps "See more" to expand. */
  const [showAllAiDrafts, setShowAllAiDrafts] = useState(false);
  const [photoLightboxId, setPhotoLightboxId] = useState<string | null>(null);
  /** Plain capture — used by row buttons. No AI analysis after upload. */
  const photoInputRef = useRef<HTMLInputElement>(null);
  /** AI capture — used by the main Photo FAB. Triggers analysis after upload. */
  const photoInputAIRef = useRef<HTMLInputElement>(null);

  // Register the AI-enabled trigger so the FAB can open this component's
  // camera and run analysis. Two separate inputs (not a flag) so cancelling
  // the picker on the FAB path can never bleed into a subsequent row capture.
  React.useEffect(() => {
    onRegisterPhotoTrigger?.(() => photoInputAIRef.current?.click());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component.id]);

  const photoLightbox = localPhotos.find((p) => p.id === photoLightboxId) ?? null;

  const status = resolveStatus(component.inspectionStatus, componentFindings.length);

  // The catch-all component on each section (per #59) collects section-wide
  // notes/photos/findings — it's never "to inspect / not inspected / not
  // present" in the way a physical component is. Hide the status pill on it
  // so the inspector isn't cycling a meaningless state machine. Name-based
  // detection is fine here; "General" is the only catch-all today.
  const isCatchAllComponent = component.name === 'General';

  const handleCycleStatus = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (readOnly || cycling) return;
    // Always advance from the *stored* status so tapping through "O" returns
    // to the underlying state the inspector previously set.
    const next = nextStatus(component.inspectionStatus);
    setCycling(true);
    try {
      const result = await inspectionsApi.setComponentStatus(inspectionId, component.id, next);
      if (result.success && result.data) {
        onComponentChanged(result.data);
      }
    } finally {
      setCycling(false);
    }
  };

  const handleAddFinding = async () => {
    const title = newFindingTitle.trim();
    if (!title) return;
    setSubmittingFinding(true);
    try {
      const description = newFindingDescription.trim() || undefined;
      const result = await inspectionsApi.addComponentFinding(
        inspectionId,
        roomId,
        component.id,
        { title, description, severity: 'recommendation' },
      );
      if (result.success && result.data) {
        onFindingAdded(result.data);
      }
      setNewFindingTitle('');
      setNewFindingDescription('');
      setAddingFinding(false);
    } finally {
      setSubmittingFinding(false);
    }
  };

  const handleAddComment = async () => {
    const trimmed = commentBody.trim();
    if (!trimmed || !addingComment) return;
    setSubmittingComment(true);
    try {
      const res = await inspectionsApi.addComponentComment(inspectionId, component.id, {
        kind: addingComment,
        body: trimmed,
      });
      if (res.success && res.data) {
        onCommentAdded(component.id, res.data);
      }
      setCommentBody('');
      setAddingComment(null);
    } finally {
      setSubmittingComment(false);
    }
  };

  /** Run AI vision analysis on an already-uploaded photo. Extracted so both
   *  the FAB capture path and the per-thumbnail "Analyze with AI" button can
   *  call it without duplicating logic. */
  const runAIAnalysis = async (uploadedPhoto: ComponentPhoto) => {
    // Clear any prior "nothing flagged" state before re-running
    setNoFindingPhotoIds((prev) => { const n = new Set(prev); n.delete(uploadedPhoto.id); return n; });
    setAnalyzingPhotoIds((prev) => new Set(prev).add(uploadedPhoto.id));
    try {
      const result = await inspectionsApi.analyzeComponentPhoto(
        inspectionId,
        roomId,
        component.id,
        uploadedPhoto.id,
      );

      const { findings } = result as {
        findings: FindingWithConfidence[];
        annotations: ComponentPhotoAnnotation[];
      };

      if (findings.length === 0) {
        setNoFindingPhotoIds((prev) => new Set(prev).add(uploadedPhoto.id));
      } else {
        // Photo has been claimed as finding evidence — remove from component strip
        setLocalPhotos((prev) => prev.filter((p) => p.id !== uploadedPhoto.id));
        // New AI suggestions arrived — collapse back to "top 1" view so the
        // inspector isn't immediately confronted with all of them.
        setShowAllAiDrafts(false);
        // Store confidence for each finding (session-only — not persisted)
        setFindingConfidences((prev) => {
          const next = new Map(prev);
          for (const f of findings) {
            if (typeof f.confidence === 'number') next.set(f.id, f.confidence);
          }
          return next;
        });
        // Bubble each draft finding up to InspectionView
        for (const f of findings) {
          onFindingAdded(f);
        }
      }
    } catch {
      // Analysis failure is non-fatal — photo is already uploaded and usable.
      setNoFindingPhotoIds((prev) => new Set(prev).add(uploadedPhoto.id));
    } finally {
      setAnalyzingPhotoIds((prev) => {
        const next = new Set(prev);
        next.delete(uploadedPhoto.id);
        return next;
      });
    }
  };

  /** Shared upload logic — `withAI` determines whether analysis runs after. */
  const handlePhotoUpload = async (file: File, withAI: boolean) => {
    setUploadingPhoto(true);
    let photo: ComponentPhoto | null = null;
    try {
      photo = await inspectionsApi.addComponentPhoto(inspectionId, roomId, component.id, file);
      setLocalPhotos((prev) => [...prev, photo!]);
    } finally {
      setUploadingPhoto(false);
    }
    if (!photo) return;
    if (withAI) await runAIAnalysis(photo);
  };

  /** Row-button path — upload only, no AI. */
  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await handlePhotoUpload(file, false);
  };

  /** FAB path — upload then AI analysis. */
  const handlePhotoSelectWithAI = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await handlePhotoUpload(file, true);
  };

  const handleDeletePhoto = async (photo: ComponentPhoto) => {
    setDeletingPhotoId(photo.id);
    try {
      await inspectionsApi.deleteComponentPhoto(inspectionId, roomId, component.id, photo.id);
      setLocalPhotos((prev) => prev.filter((p) => p.id !== photo.id));
    } finally {
      setDeletingPhotoId(null);
    }
  };

  return (
    <div
      className={cn(
        'transition-colors',
        isActive ? 'bg-brand-soft/30' : 'bg-surface-1',
      )}
    >
      <button
        type="button"
        onClick={onFocus}
        className={cn(
          'w-full flex items-center justify-between gap-3 px-4 text-left transition-colors',
          'min-h-[52px] py-2 hover:bg-surface-2/60',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand',
        )}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <svg
            className={cn(
              'w-4 h-4 text-ink-subtle transition-transform shrink-0',
              isActive && 'rotate-90',
            )}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-body-md font-medium text-ink truncate">{component.name}</span>
          {componentFindings.length > 0 && (
            <Badge tone="brand" compact aria-label={`${componentFindings.length} findings`}>
              {componentFindings.length}
            </Badge>
          )}
        </div>
        {!isCatchAllComponent && (
          <span
            role="button"
            tabIndex={readOnly ? -1 : 0}
            onClick={handleCycleStatus}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCycleStatus(e);
              }
            }}
            title={STATUS_TITLE[status]}
            className={cn(
              'shrink-0 rounded-full transition-opacity',
              readOnly ? 'cursor-default' : 'cursor-pointer',
              cycling && 'opacity-60',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
            )}
          >
            <StatusPill status={status} />
          </span>
        )}
      </button>

      {isActive && (
        // Flatten: attributes used to render inside a nested bordered card,
        // creating double border + double padding inside the component card.
        // The expanded body now uses the component card's own surface; an
        // internal divider keeps the attributes group visually distinct
        // without a second box. space-y-2 (was -3) tightens the rhythm —
        // line-height 1.6 on body-md is already generous breathing room.
        <div className="px-4 pb-4 pt-1 space-y-2 border-t border-border-subtle bg-surface-1">
          {(component.attributes.length > 0 || !readOnly) && (
            <div className="divide-y divide-border-subtle">
              {component.attributes.map((attr) => (
                <AttributeField
                  key={attr.id}
                  inspectionId={inspectionId}
                  componentId={component.id}
                  attribute={attr}
                  schemaOptions={lookupSchemaOptions(attr.key)}
                  onNeedSchema={ensureSchema}
                  readOnly={readOnly}
                  onAttributeChanged={onAttributeChanged}
                  onAttributeDeleted={(attrId) => onAttributeDeleted(component.id, attrId)}
                />
              ))}
              {!readOnly && (
                <AddAttributeRow
                  inspectionId={inspectionId}
                  componentId={component.id}
                  onAttributeAdded={(attr) => onAttributeAdded(component.id, attr)}
                />
              )}
            </div>
          )}

          {/*
           * Comments (notes + limitations) surface the templated prose
           * that snapshotted into this inspection, plus any day-of
           * observations the inspector types in. Uncheck a templated row
           * to exclude it from the report without deleting it — template
           * is never touched from the inspection side.
           */}
          <ComponentCommentsSection
            inspectionId={inspectionId}
            component={component}
            readOnly={readOnly}
            onCommentChanged={onCommentChanged}
            onCommentDeleted={onCommentDeleted}
          />

          {/* Component photo strip — standalone documentation photos, no finding required.
              The outer space-y-2 wrapper was dropped along with the standalone Add-photo
              pill (now in the unified add-row at the bottom of the card). */}
          {localPhotos.length > 0 && (
            <div className="flex flex-wrap gap-2">
                  {localPhotos.map((photo) => {
                    const isAnalyzing = analyzingPhotoIds.has(photo.id);
                    const nothingFlagged = noFindingPhotoIds.has(photo.id);
                    // Build annotation groups for overlay: group by finding_id so each
                    // finding gets its own color index.
                    const annotationsByFinding = new Map<string, { index: number; severity: Finding['severity']; annotations: ComponentPhotoAnnotation[] }>();
                    for (const ann of (photo.annotations ?? [])) {
                      const match = componentFindings.find((f) => f.id === ann.findingId);
                      // Skip annotations whose finding was deleted/ignored — avoids
                      // orphaned boxes on the thumbnail after the inspector taps Ignore.
                      if (!match) continue;
                      if (!annotationsByFinding.has(ann.findingId)) {
                        annotationsByFinding.set(ann.findingId, {
                          index: annotationsByFinding.size,
                          severity: match.severity,
                          annotations: [],
                        });
                      }
                      annotationsByFinding.get(ann.findingId)!.annotations.push(ann);
                    }
                    const overlayFindings: BBoxFinding[] = Array.from(annotationsByFinding.values()).map((g) => ({
                      index: g.index,
                      severity: g.severity,
                      annotations: g.annotations.map((a) => ({
                        type: a.type,
                        // Convert from DB integers (×10000) to fractions (0–1)
                        x1: a.x1 / 10000,
                        y1: a.y1 / 10000,
                        x2: a.x2 / 10000,
                        y2: a.y2 / 10000,
                        rotationDeg: a.rotationDeg,
                      })),
                    }));

                    return (
                    <div key={photo.id} className="relative group w-16 h-16 shrink-0">
                      <img
                        src={photo.url}
                        alt={photo.caption ?? component.name}
                        className="w-16 h-16 object-cover rounded-md cursor-pointer border border-border-subtle"
                        onClick={() => setPhotoLightboxId(photo.id)}
                      />

                      {/* Bounding-box annotation overlay */}
                      {overlayFindings.length > 0 && !isAnalyzing && (
                        <BBoxAnnotationOverlay findings={overlayFindings} />
                      )}

                      {/* AI analyzing spinner overlay */}
                      {isAnalyzing && (
                        <div className="absolute inset-0 rounded-md bg-black/50 flex flex-col items-center justify-center gap-1">
                          <svg className="w-4 h-4 animate-spin text-white" viewBox="0 0 24 24" aria-hidden>
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          <span className="text-[9px] font-medium text-white/90 leading-none">Analyzing</span>
                        </div>
                      )}

                      {/* "Nothing flagged" badge — clears after the inspector interacts */}
                      {nothingFlagged && !isAnalyzing && overlayFindings.length === 0 && (
                        <div
                          className="absolute inset-0 rounded-md bg-black/40 flex items-end justify-center pb-1 pointer-events-none"
                          aria-label="AI found nothing to flag"
                        >
                          <span className="text-[9px] font-medium text-white/80 leading-none px-1 text-center">
                            Nothing flagged
                          </span>
                        </div>
                      )}

                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => handleDeletePhoto(photo)}
                          disabled={deletingPhotoId === photo.id}
                          className={cn(
                            'absolute -top-2 -right-2 w-6 h-6 bg-sev-critical text-white rounded-full',
                            'items-center justify-center flex opacity-0 group-hover:opacity-100 focus:opacity-100',
                            'transition-opacity hover:bg-sev-critical-ink disabled:opacity-50 shadow',
                          )}
                          title="Remove photo"
                          aria-label="Remove photo"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}

                      {/* "Analyze with AI" on-demand button — only on photos
                          without annotations, hidden until hover/focus.
                          Lets inspectors skip AI on some photos and opt-in later. */}
                      {!readOnly && !isAnalyzing && overlayFindings.length === 0 && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); void runAIAnalysis(photo); }}
                          className={cn(
                            'absolute -bottom-2 -right-2 w-6 h-6 bg-brand text-white rounded-full shadow',
                            'flex items-center justify-center',
                            'opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity',
                            'hover:brightness-90 active:brightness-75',
                          )}
                          title="Analyze with AI"
                          aria-label="Analyze with AI"
                        >
                          {/* Sparkle icon — Heroicons "sparkles" outline */}
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                          </svg>
                        </button>
                      )}
                    </div>
                    );
                  })}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className={cn(
                        'w-16 h-16 shrink-0 rounded-md border-2 border-dashed border-border',
                        'flex items-center justify-center text-ink-subtle',
                        'hover:border-brand/50 hover:text-brand hover:bg-brand-soft/30 transition-colors',
                        'disabled:opacity-50',
                      )}
                      title="Add photo"
                      aria-label="Add component photo"
                    >
                      {uploadingPhoto ? (
                        <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" aria-hidden>
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                    </button>
                  )}
            </div>
          )}

          {componentFindings.length > 0 && (() => {
            const aiDrafts = componentFindings.filter((f) => f.aiDraft);
            const regular = componentFindings.filter((f) => !f.aiDraft);
            // Show the top AI draft immediately; reveal the rest on demand so
            // the inspector isn't confronted with multiple boxes at once.
            const visibleDrafts = showAllAiDrafts ? aiDrafts : aiDrafts.slice(0, 1);
            const hiddenDraftCount = aiDrafts.length - visibleDrafts.length;

            const handleDelete = (id: string) => {
              setFindingConfidences((prev) => {
                const next = new Map(prev);
                next.delete(id);
                return next;
              });
              onFindingDeleted(id);
            };

            return (
              <div className="space-y-2">
                {regular.map((finding) => (
                  <FindingCard
                    key={finding.id}
                    finding={finding}
                    inspectionId={inspectionId}
                    onUpdated={onFindingUpdated}
                    onDeleted={handleDelete}
                    onSaveToLibrary={onSaveToLibrary}
                  />
                ))}
                {visibleDrafts.map((finding) => (
                  <FindingCard
                    key={finding.id}
                    finding={finding}
                    inspectionId={inspectionId}
                    confidence={findingConfidences.get(finding.id)}
                    onUpdated={onFindingUpdated}
                    onDeleted={handleDelete}
                    onSaveToLibrary={onSaveToLibrary}
                  />
                ))}
                {hiddenDraftCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllAiDrafts(true)}
                    className={cn(
                      'w-full flex items-center justify-center gap-1.5 py-2 rounded-lg',
                      'border border-dashed border-brand/30 text-caption text-brand/70',
                      'hover:border-brand/50 hover:text-brand hover:bg-brand-soft/20 transition-colors',
                    )}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15M14.25 3.104c.251.023.501.05.75.082M19.8 15l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.607L5 14.5m14.8.5l.392 1.571A9 9 0 0112 18a9 9 0 01-7.192-3.429L5 14.5" />
                    </svg>
                    See {hiddenDraftCount} more AI suggestion{hiddenDraftCount > 1 ? 's' : ''}
                  </button>
                )}
              </div>
            );
          })()}

          {!readOnly && component.name.trim().toLowerCase() !== 'general' && (
            // Per-component suggestion strip — chips are scoped to this
            // component so when the inspector taps one, the created finding
            // attaches here instead of landing at the room level.
            //
            // Hidden on the section's General catch-all (#59 follow-up):
            // the suggestion library is keyed on specific component names
            // (Dishwasher, Roof Covering, etc.), so "General" produces
            // either no chips or noisy ones. Inspectors hit Add finding
            // directly on General when they have something section-level
            // to capture.
            <SuggestionsStrip
              sectionName={roomName}
              componentName={component.name}
              existingFindingTitles={componentFindings.map((f) => f.title)}
              refreshKey={librarySaveVersion}
              onSuggestionSelected={({ title, description }) => {
                setNewFindingTitle(title);
                setNewFindingDescription(description ?? '');
                setAddingFinding(true);
              }}
              onError={onSuggestionError}
            />
          )}

          {!readOnly && (
            <div>
              {addingFinding ? (
                <div className="p-3 bg-brand-soft/40 border border-brand/20 rounded-lg space-y-2">
                  <Input
                    inputSize="sm"
                    value={newFindingTitle}
                    onChange={(e) => setNewFindingTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newFindingTitle.trim() && !newFindingDescription) handleAddFinding();
                      if (e.key === 'Escape') {
                        setAddingFinding(false);
                        setNewFindingTitle('');
                        setNewFindingDescription('');
                      }
                    }}
                    placeholder={`What did you find on the ${component.name}?`}
                    autoFocus
                  />
                  <textarea
                    value={newFindingDescription}
                    onChange={(e) => setNewFindingDescription(e.target.value)}
                    placeholder="Details (optional — edit or clear)"
                    rows={2}
                    className={cn(
                      'w-full resize-none rounded-md border border-border bg-surface px-3 py-1.5',
                      'text-sm text-ink placeholder:text-ink-subtle',
                      'focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent',
                    )}
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleAddFinding}
                      disabled={!newFindingTitle.trim() || submittingFinding}
                      loading={submittingFinding}
                      className="flex-1"
                      size="sm"
                    >
                      Add
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setAddingFinding(false);
                        setNewFindingTitle('');
                        setNewFindingDescription('');
                      }}
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : addingComment ? (
                <div className="p-3 bg-surface-2/40 border border-border-subtle rounded-lg space-y-2">
                  <Input
                    inputSize="sm"
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && commentBody.trim()) handleAddComment();
                      if (e.key === 'Escape') {
                        setAddingComment(null);
                        setCommentBody('');
                      }
                    }}
                    placeholder={
                      addingComment === 'limitation'
                        ? `What part of ${component.name} couldn't you fully inspect?`
                        : `Observation about ${component.name}…`
                    }
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleAddComment}
                      disabled={!commentBody.trim() || submittingComment}
                      loading={submittingComment}
                      className="flex-1"
                      size="sm"
                    >
                      Add {addingComment === 'limitation' ? 'limitation' : 'note'}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setAddingComment(null);
                        setCommentBody('');
                      }}
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                /* Unified add-row — Photo / Finding / Note / Limitation in
                   one band so all "what would I add next?" affordances sit
                   together at the bottom of the component card. */
                <div className="flex flex-wrap gap-2">
                  <AddItemPill
                    label="Photo"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    icon={
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    }
                  />
                  <AddItemPill
                    label="Finding"
                    onClick={() => setAddingFinding(true)}
                  />
                  <AddItemPill
                    label="Note"
                    onClick={() => setAddingComment('note')}
                  />
                  <AddItemPill
                    label="Limitation"
                    tone="amber"
                    onClick={() => setAddingComment('limitation')}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Hidden file input — row button path, no AI analysis */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handlePhotoSelect}
      />
      {/* Hidden file input — FAB path, triggers AI analysis after upload */}
      <input
        ref={photoInputAIRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handlePhotoSelectWithAI}
      />

      {/* Photo lightbox — shows annotation overlay at full size */}
      {photoLightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={() => setPhotoLightboxId(null)}
        >
          {/* Wrapper: relative so the SVG overlay can be positioned absolutely */}
          <div
            className="relative max-w-full max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photoLightbox.url}
              alt={photoLightbox.caption ?? component.name}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            {/* Full-size annotation overlay */}
            {(() => {
              const annotationsByFinding = new Map<string, { index: number; severity: Finding['severity']; annotations: ComponentPhotoAnnotation[] }>();
              for (const ann of (photoLightbox.annotations ?? [])) {
                const match = componentFindings.find((f) => f.id === ann.findingId);
                if (!match) continue;
                if (!annotationsByFinding.has(ann.findingId)) {
                  annotationsByFinding.set(ann.findingId, {
                    index: annotationsByFinding.size,
                    severity: match.severity,
                    annotations: [],
                  });
                }
                annotationsByFinding.get(ann.findingId)!.annotations.push(ann);
              }
              const overlayFindings: BBoxFinding[] = Array.from(annotationsByFinding.values()).map((g) => ({
                index: g.index,
                severity: g.severity,
                annotations: g.annotations.map((a) => ({
                  type: a.type,
                  x1: a.x1 / 10000,
                  y1: a.y1 / 10000,
                  x2: a.x2 / 10000,
                  y2: a.y2 / 10000,
                  rotationDeg: a.rotationDeg,
                })),
              }));
              return overlayFindings.length > 0 ? (
                <BBoxAnnotationOverlay findings={overlayFindings} />
              ) : null;
            })()}
          </div>
          <button
            type="button"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
            onClick={() => setPhotoLightboxId(null)}
            aria-label="Close lightbox"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

// ── AttributeField ────────────────────────────────────────────────────────────
// Unified mobile-first attribute editor. All field types share the same
// label + input layout; AI-generated option chips appear *below* the input
// when it's focused. This works for new and existing components — schema is
// fetched lazily on first focus via the parent's `ensureSchema` callback,
// which is a no-op on subsequent calls (cached in a ref at the row level).
//
//   select / text  — text input; chips appear on focus if options exist.
//   number         — decimal input with right-aligned unit label.

interface AttributeFieldProps {
  inspectionId: string;
  componentId: string;
  attribute: ComponentAttribute;
  /** Options resolved from the AI schema for this attribute key. Null when
   *  the schema hasn't been fetched yet or the field has no discrete options. */
  schemaOptions: string[] | null;
  /** Trigger schema fetch (no-op after first call). Called on first focus. */
  onNeedSchema: () => Promise<void>;
  readOnly: boolean;
  onAttributeChanged: (attribute: ComponentAttribute) => void;
  onAttributeDeleted: (attributeId: string) => void;
}

const AttributeField: React.FC<AttributeFieldProps> = ({
  inspectionId,
  componentId,
  attribute,
  schemaOptions,
  onNeedSchema,
  readOnly,
  onAttributeChanged,
  onAttributeDeleted,
}) => {
  const [draft, setDraft] = useState<string>(attribute.value ?? '');
  const [saving, setSaving] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  React.useEffect(() => {
    setDraft(attribute.value ?? '');
  }, [attribute.value, attribute.id]);

  const commit = async (override?: string) => {
    const source = override ?? draft;
    const trimmed = source.trim();
    const nextValue = trimmed === '' ? null : trimmed;
    if (nextValue === (attribute.value ?? null)) return;
    setSaving(true);
    try {
      const result = await inspectionsApi.updateComponentAttribute(
        inspectionId,
        componentId,
        attribute.id,
        nextValue,
      );
      if (result.success && result.data) onAttributeChanged(result.data);
    } finally {
      setSaving(false);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Kick off schema fetch if no options are loaded yet. The parent dedupes.
    if (!schemaOptions) void onNeedSchema();
  };

  const handleBlur = () => {
    setIsFocused(false);
    void commit();
  };

  // onPointerDown with preventDefault keeps the input focused (no blur) while
  // registering the chip tap — same technique as the old inline pill grid.
  const handleChipPointerDown = (opt: string) => (e: React.PointerEvent) => {
    e.preventDefault();
    const next = draft === opt ? '' : opt; // second tap clears
    setDraft(next);
    void commit(next);
    setIsFocused(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await inspectionsApi.deleteComponentAttribute(inspectionId, componentId, attribute.id);
      onAttributeDeleted(attribute.id);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const displayLabel = attribute.label ?? attribute.key;
  const type = attribute.type ?? 'text';
  const isEmpty = draft.trim() === '';
  const showChips = isFocused && !readOnly && schemaOptions && schemaOptions.length > 0;

  const inputCls = cn(
    'w-full text-body-sm text-ink placeholder:text-ink-faint',
    'rounded-md px-2 py-1.5 border transition-colors',
    readOnly
      ? 'border-transparent bg-transparent'
      : cn(
          isEmpty
            ? 'border-dashed border-border bg-surface-2/60 hover:bg-surface-2 hover:border-brand/40'
            : 'border-border-subtle bg-surface-1 hover:border-brand/40',
          'focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20',
        ),
    saving && 'opacity-60',
  );

  return (
    <div className={cn('px-3 py-2.5 border-b border-border-subtle last:border-b-0', (saving || deleting) && 'opacity-60')}>
      {/* Label row — full width so it never truncates */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <label
          htmlFor={`attr-${attribute.id}`}
          className="text-caption font-medium text-ink-subtle leading-snug"
        >
          {displayLabel}
        </label>

        {/* Delete control — hidden in readOnly */}
        {!readOnly && (
          confirmDelete ? (
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                disabled={deleting}
                onClick={() => void handleDelete()}
                className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center transition-colors',
                  'bg-sev-critical text-white hover:bg-sev-critical-ink disabled:opacity-50',
                )}
                title="Confirm delete"
                aria-label="Confirm delete attribute"
              >
                {deleting ? (
                  <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="w-7 h-7 rounded-full flex items-center justify-center text-ink-subtle hover:text-ink hover:bg-surface-2 transition-colors"
                title="Cancel"
                aria-label="Cancel delete"
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-ink-faint hover:text-sev-critical hover:bg-sev-critical/10 transition-colors"
              title="Delete attribute"
              aria-label="Delete attribute"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )
        )}
      </div>

      {/* Input — full width below the label */}
      {type === 'number' ? (
        <div className="flex items-center gap-1.5">
          <input
            id={`attr-${attribute.id}`}
            type="text"
            inputMode="decimal"
            value={draft}
            readOnly={readOnly}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            placeholder={readOnly ? '—' : '0'}
            className={cn(
              'w-24 text-body-sm text-ink text-right placeholder:text-ink-faint',
              'rounded-md px-2 py-1.5 border transition-colors',
              readOnly
                ? 'border-transparent bg-transparent'
                : cn(
                    isEmpty
                      ? 'border-dashed border-border bg-surface-2/60 hover:bg-surface-2 hover:border-brand/40'
                      : 'border-border-subtle bg-surface-1 hover:border-brand/40',
                    'focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20',
                  ),
            )}
          />
          {attribute.unit && (
            <span className="text-caption text-ink-subtle whitespace-nowrap">{attribute.unit}</span>
          )}
        </div>
      ) : (
        <input
          id={`attr-${attribute.id}`}
          type="text"
          value={draft}
          readOnly={readOnly}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          placeholder={readOnly ? '—' : 'Tap to add…'}
          className={inputCls}
        />
      )}

      {/* Suggestion chips — appear below the input while it's focused */}
      {showChips && (
        <div className="flex flex-wrap gap-2 pt-2 pb-1">
          {schemaOptions!.map((opt) => {
            const selected = draft === opt;
            return (
              <button
                key={opt}
                type="button"
                disabled={saving}
                onPointerDown={handleChipPointerDown(opt)}
                className={cn(
                  'h-8 px-3 rounded-full border text-caption font-medium transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
                  selected
                    ? 'bg-brand text-white border-brand'
                    : 'bg-surface-2 text-ink border-border-subtle hover:border-brand/40 hover:bg-surface-3',
                )}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── AddAttributeRow ───────────────────────────────────────────────────────────
// Simple + button that expands into a single label input. The value is left
// blank — the inspector fills it via the regular AttributeField editor.
// Custom attributes are instance-only and never feed back to the schema cache.

interface AddAttributeRowProps {
  inspectionId: string;
  componentId: string;
  onAttributeAdded: (attribute: ComponentAttribute) => void;
}

const AddAttributeRow: React.FC<AddAttributeRowProps> = ({
  inspectionId,
  componentId,
  onAttributeAdded,
}) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOpen = () => {
    setOpen(true);
    setLabel('');
    setTimeout(() => inputRef.current?.focus(), 40);
  };

  const handleSave = async () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const result = await inspectionsApi.addComponentAttribute(
        inspectionId,
        componentId,
        trimmed,
        null,
      );
      if (result.success && result.data) {
        onAttributeAdded(result.data);
        setOpen(false);
        setLabel('');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        className={cn(
          'w-full flex items-center justify-center min-h-[44px]',
          'text-ink-faint hover:text-brand hover:bg-brand-soft/30 transition-colors',
        )}
        aria-label="Add attribute"
        title="Add attribute"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 min-h-[44px] border-t border-border-subtle">
      <input
        ref={inputRef}
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void handleSave();
          if (e.key === 'Escape') { setOpen(false); setLabel(''); }
        }}
        placeholder="Label…"
        className={cn(
          'flex-1 text-body-sm text-ink placeholder:text-ink-faint rounded-md px-2 py-1.5',
          'border border-border bg-surface-1 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20',
        )}
      />
      <button
        type="button"
        disabled={!label.trim() || saving}
        onClick={() => void handleSave()}
        className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center bg-brand text-white shrink-0',
          'hover:bg-brand/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors',
        )}
        aria-label="Save attribute"
      >
        {saving ? (
          <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" aria-hidden>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); setLabel(''); }}
        className="w-7 h-7 rounded-full flex items-center justify-center text-ink-subtle hover:text-ink hover:bg-surface-2 transition-colors shrink-0"
        aria-label="Cancel"
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
};

// ── AddItemPill ───────────────────────────────────────────────────────────
// Single visual atom for the four "+ Photo / + Finding / + Note / + Limitation"
// buttons in the unified add-row. Brand-tinted dashed border by default;
// the `tone="amber"` variant marks limitations to mirror the warning color
// used elsewhere in the comment list.

interface AddItemPillProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'brand' | 'amber';
  icon?: React.ReactNode;
}

const AddItemPill: React.FC<AddItemPillProps> = ({
  label, onClick, disabled, tone = 'brand', icon,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-caption font-medium',
      'border border-dashed bg-transparent transition-colors',
      'focus-visible:outline-none focus-visible:ring-2',
      tone === 'amber'
        ? 'border-amber-400 text-amber-700 hover:bg-amber-50 focus-visible:ring-amber-500'
        : 'border-brand/40 text-brand hover:bg-brand-soft/60 focus-visible:ring-brand',
      'disabled:opacity-50',
    )}
  >
    {icon ?? (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
      </svg>
    )}
    {label}
  </button>
);

export default ComponentList;
